import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// NOTE: No react-toastify here. Redux reducers must be pure functions
// that only compute the next state. All toast notifications belong in
// the component (or async thunk) that dispatches actions.

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  inventory?: number; // Optional: track available stock
  shippingType?: "Free" | "Fixed" | "Negotiable";
  shippingFee?: number;
  vendorId?: string;
}

interface CartState {
  items: CartItem[];
  couponCode: string;
  discount: number;
}

const initialState: CartState = {
  items: [],
  couponCode: "",
  discount: 0,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: (
      state,
      action: PayloadAction<Omit<CartItem, "quantity"> & { inventory?: number }>,
    ) => {
      const { id, name, price, image, inventory = Infinity, shippingType, shippingFee, vendorId } = action.payload;

      // Guard: silently skip if inventory is exhausted.
      // The component is responsible for checking stock and showing
      // the appropriate toast before dispatching this action.
      if (inventory !== undefined && inventory <= 0) {
        return;
      }

      const existingItem = state.items.find((item) => item.id === id);

      if (existingItem) {
        // Silently cap at the available inventory.
        if (inventory !== undefined && existingItem.quantity >= inventory) {
          return;
        }
        existingItem.quantity += 1;
      } else {
        state.items.push({ id, name, price, image, quantity: 1, shippingType, shippingFee, vendorId });
      }
    },

    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },

    updateQuantity: (
      state,
      action: PayloadAction<{ id: string; quantity: number; inventory?: number }>,
    ) => {
      const item = state.items.find((item) => item.id === action.payload.id);
      if (item) {
        const newQty = action.payload.quantity;
        const available = action.payload.inventory ?? Infinity;

        // Clamp to available stock — component handles the user-facing error.
        if (newQty > available) {
          item.quantity = available;
          return;
        }
        if (newQty <= 0) {
          state.items = state.items.filter((i) => i.id !== action.payload.id);
        } else {
          item.quantity = newQty;
        }
      }
    },

    // Used to sync the entire cart from the backend response.
    // Call this after addToCart / removeFromCart / login to keep
    // Redux state in perfect sync with the server.
    setCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
    },

    applyCoupon: (
      state,
      action: PayloadAction<{ code: string; discount: number }>,
    ) => {
      state.couponCode = action.payload.code;
      state.discount = action.payload.discount;
    },

    removeCoupon: (state) => {
      state.couponCode = "";
      state.discount = 0;
    },

    clearCart(state) {
      state.items = [];
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  setCartItems,
  applyCoupon,
  removeCoupon,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;